/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react'
import { Fragment, ReactElement, useContext, useEffect } from 'react';
import { useInView } from 'react-intersection-observer'
import { FeaturedArticleShowContext } from '../../FeaturedArticles/Show'
import { LeafletForLeafletsComponentProp } from '../../types'

type Props = {
  leaflet: LeafletForLeafletsComponentProp
  order: number
  viewMore: boolean
}

function LeafletComponent({ leaflet, order, viewMore }: Props): ReactElement {
  const leafletStyle = () =>
    css`
      box-sizing: border-box;
      width: 50%;
      padding: ${order % 2 === 0 ? '12px 6px 12px 0' : '12px 0 12px 6px'};
      display: ${order > 3 && !viewMore ? 'none' : 'block'};
    `

  // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-assignment
  const { ref, inView } = useInView({ triggerOnce: true })
  const featuredArticleShowContext = useContext(FeaturedArticleShowContext)

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    inView &&
      featuredArticleShowContext &&
      featuredArticleShowContext.logFeaturedArticleEvent({
        event: 'leaflet_component_imp',
        contentType: leaflet.className,
        contentId: leaflet.id,
        shopId: leaflet.shop.id,
      })
  }, [inView, leaflet, featuredArticleShowContext])

  const leafletClick = () => {
    featuredArticleShowContext.logFeaturedArticleEvent({
      event: 'leaflet_component_click',
      contentType: leaflet.className,
      contentId: leaflet.id,
      shopId: leaflet.shop.id,
    })
  }

  return (
    <Fragment>
      {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
      <li ref={ref} css={leafletStyle}>
        <a href={leaflet.path} css={leafletLinkStyle} onClick={leafletClick}>
          <div css={leafletImageWrapperStyle}>
            <img src={leaflet.image.url} width="170" height="90" css={leafletImageStyle} />
            <div css={countStyle}>全{leaflet.count}枚</div>
          </div>
          <div css={leafletTextWrapperStyle}>
            <div css={shopLogoWrapperStyle}>
              <img src={leaflet.shop.image.url} css={shopLogoStyle} />
            </div>
            <div css={{ flex: 1 }}>
              {leaflet.title && <div css={leafletTitleStyle}>{leaflet.title}</div>}
              <div css={shopNameStyle}>{leaflet.shop.name}</div>
            </div>
          </div>
        </a>
      </li>
    </Fragment>
  )
}

const leafletLinkStyle = css`
  display: block;
  color: rgba(0, 0, 0, 0.87);
  text-decoration: none;
`

const leafletImageWrapperStyle = css`
  border-radius: 4px;
  position: relative;
`

const leafletImageStyle = css`
  display: block;
  width: 100%;
  height: auto;
  border-radius: 4px;
`

const countStyle = css`
  position: absolute;
  top: 0;
  left: 0;
  background: rgba(0, 0, 0, 0.54);
  font-weight: bold;
  font-size: 12px;
  color: #ffffff;
  padding: 2px 4px;
  border-radius: 4px 0 4px 0;
`

const leafletTextWrapperStyle = css`
  display: flex;
  margin-top: 8px;
`

const shopLogoWrapperStyle = css`
  width: 32px;
  height: 32px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 32px;
  position: relative;
  margin-right: 6px;
`

const shopLogoStyle = css`
  display: block;
  max-width: 70.71%;
  max-height: 70.71%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`

const leafletTitleStyle = css`
  font-weight: bold;
  font-size: 12px;
  line-height: 130%;
`

const shopNameStyle = css`
  font-size: 12px;
  line-height: 130%;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
`

export default LeafletComponent
